<template>
  <div class="page-editor">
    <CRow class="align-items-start">
      <CCol col="12" xl="9">
        <CCard class="pr-0">
          <CCardBody>
            <a-card class="edit-card" style="margin:0;" :body-style="{padding:0}">
              <span slot="title">
                <h6 class="w-100 mb-0 text-muted">Nástroje</h6>
                BIS API Explorer
              </span>

              <div class="p-4">
                <!-- Request builder -->
                <div class="d-flex align-items-end flex-wrap" style="gap:10px;">
                  <div>
                    <label class="filter-label">Typ</label>
                    <a-select v-model="reqMethod" style="width:100px;">
                      <a-select-option value="read">READ</a-select-option>
                      <a-select-option value="send">SEND</a-select-option>
                    </a-select>
                  </div>
                  <div style="flex:1;">
                    <label class="filter-label">Endpoint</label>
                    <a-auto-complete v-model="endpoint" :data-source="filteredEndpoints" placeholder="napr. VratOsobu" @select="onEndpointSelect" style="width:100%;" />
                  </div>
                  <div style="min-width:250px;">
                    <label class="filter-label">Parametre</label>
                    <a-input v-model="params" placeholder="?Id=1623&StrankaId=1" @pressEnter="sendRequest" />
                  </div>
                  <a-button type="primary" :loading="loading" @click="sendRequest">Odoslať</a-button>
                </div>

                <!-- Body pre SEND -->
                <div v-if="reqMethod === 'send'" class="mt-3">
                  <label class="filter-label">Request Body (JSON)</label>
                  <a-textarea v-model="body" :rows="6" placeholder='{"id": 0, "email": "test@test.com"}' style="font-family:monospace;font-size:12px;" />
                </div>

                <!-- Response -->
                <div v-if="response !== null" class="mt-4">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <a-tag :color="response.success ? 'green' : 'red'">{{ response.success ? 'OK' : 'ERROR' }}</a-tag>
                      <span class="text-muted small ml-2">{{ responseTime }}ms</span>
                      <span class="text-muted small ml-2">{{ responseSize }}</span>
                      <span class="text-muted small ml-2">{{ response.endpoint }}</span>
                    </div>
                    <div>
                      <a-button size="small" @click="copyResponse" class="mr-1">Kopírovať</a-button>
                      <a-button size="small" @click="response = null">Zavrieť</a-button>
                    </div>
                  </div>
                  <pre class="api-response">{{ formattedData }}</pre>
                </div>

                <!-- Error -->
                <div v-if="error" class="mt-4">
                  <a-alert type="error" :message="error" showIcon />
                </div>

                <!-- História -->
                <div v-if="history.length" class="mt-4">
                  <h6 class="text-muted small" style="text-transform:uppercase;letter-spacing:.04em;">História</h6>
                  <div v-for="(h, i) in history" :key="i" class="history-item" @click="loadFromHistory(h)">
                    <a-tag :color="h.method === 'read' ? 'blue' : 'orange'" size="small">{{ h.method }}</a-tag>
                    <span class="small">{{ h.endpoint }}{{ h.params }}</span>
                    <span class="text-muted small" style="float:right;">{{ h.time }}ms</span>
                  </div>
                </div>
              </div>
            </a-card>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Pravý panel - endpointy -->
      <CCol col="12" xl="3">
        <div class="sidebar-sticky">
          <CCard>
            <CCardBody class="p-3">
              <h6 class="text-muted small mb-2" style="text-transform:uppercase;letter-spacing:.04em;">READ endpointy</h6>
              <div v-for="cat in readCategories" :key="cat.name" class="mb-2">
                <div class="small text-muted fw-600 mb-1">{{ cat.name }}</div>
                <div v-for="ep in cat.endpoints" :key="ep.name" class="ep-item" @click="selectEndpoint('read', ep)">
                  {{ ep.name }}
                </div>
              </div>

              <a-divider class="my-2" />

              <h6 class="text-muted small mb-2" style="text-transform:uppercase;letter-spacing:.04em;">WRITE endpointy</h6>
              <div v-for="cat in writeCategories" :key="cat.name" class="mb-2">
                <div class="small text-muted fw-600 mb-1">{{ cat.name }}</div>
                <div v-for="ep in cat.endpoints" :key="ep.name" class="ep-item" @click="selectEndpoint('send', ep)">
                  {{ ep.name }}
                </div>
              </div>
            </CCardBody>
          </CCard>
        </div>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { dataService } from '../_services'

const READ_ENDPOINTS = [
  { category: 'Produkty', endpoints: [
    { name: 'VratProdukty', params: '?ZnackaId=', desc: 'Zoznam produktov' },
    { name: 'VratObrazky', params: '?FoName=produkty&FoId=', desc: 'Obrázky produktov' },
    { name: 'VratDokumenty', params: '?FoName=produkty', desc: 'Dokumenty' },
    { name: 'VratVlastnosti', params: '?FoName=produkty', desc: 'Vlastnosti' },
    { name: 'VratVlastnostiTypy', params: '?FoName=produkty&StrankaId=', desc: 'Typy vlastností' },
    { name: 'VratVlastnostiSkupiny', params: '?FoName=produkty&StrankaId=', desc: 'Skupiny vlastností' },
    { name: 'VratVlastnostiPredvoleneHodnoty', params: '?FoName=produkty&StrankaId=', desc: 'Predvolené hodnoty' },
    { name: 'VratAtributyProduktov', params: '?StrankaId=', desc: 'Atribúty' },
    { name: 'VratSerieProduktov', params: '?StrankaId=', desc: 'Série' },
    { name: 'VratAkcioveCenyZakladnyCennik', params: '', desc: 'Akciové ceny' },
  ]},
  { category: 'Osoby a Firmy', endpoints: [
    { name: 'VratOsobu', params: '?Id=', desc: 'Detail osoby' },
    { name: 'VratOsobuFirmy', params: '?Id=', desc: 'Osoba vo firme' },
    { name: 'VratFirmu', params: '?Id=', desc: 'Detail firmy' },
  ]},
  { category: 'Doklady', endpoints: [
    { name: 'VratDoklad', params: '?Id=', desc: 'Detail dokladu' },
    { name: 'VratDoklady', params: '?FirmaId=', desc: 'Zoznam dokladov' },
  ]},
  { category: 'Cenníky', endpoints: [
    { name: 'VratCennikZaFirmuOsobu', params: '?FirmaId=&OsobaId=', desc: 'Cenník pre firmu/osobu' },
    { name: 'VratCennikZaFirmuOsobuProdukt', params: '?ProduktId=&FirmaId=&OsobaId=', desc: 'Cena produktu' },
    { name: 'VratZlavoveKupony', params: '', desc: 'Zľavové kupóny' },
  ]},
  { category: 'Doprava a Sklady', endpoints: [
    { name: 'VratSklady', params: '?StrankaId=', desc: 'Sklady' },
    { name: 'VratSposobyDopravy', params: '', desc: 'Spôsoby dopravy' },
    { name: 'VratCenyDopravy', params: '', desc: 'Ceny dopravy' },
  ]},
]

const WRITE_ENDPOINTS = [
  { category: 'Osoby', endpoints: [
    { name: 'UlozOsobu', params: '', body: '{"id": 0, "email": "", "telefon": "", "meno": "", "priezvisko": "", "ulica": "", "cislo": "", "mesto": "", "psc": "", "krajina": "SK"}' },
    { name: 'UlozOsobuFirmy', params: '', body: '{"id": 0, "email": "", "firmaid": 0}' },
    { name: 'UlozFirmu', params: '', body: '{"id": 0, "ico": "", "dic": "", "icdph": "", "nazov": "", "ulica": "", "cislo": "", "mesto": "", "psc": "", "krajina": "SK"}' },
  ]},
  { category: 'Doklady', endpoints: [
    { name: 'UlozDoklad', params: '', body: '{"Id": 0, "firmaId": 0, "osobaId": 0, "typOdberu": "D", "typPlatby": "E", "typSpracovania": "R"}' },
    { name: 'UlozPolozkuDokladu', params: '', body: '{"id": 0, "dokladId": 0, "produktid": 0, "mnozstvo": 1, "cenaCelkom": 0, "percentoDPH": 20}' },
    { name: 'UlozUhraduDokladu', params: '', body: '{"id": 0, "dokladId": 0, "platbaId": 0, "typ": "U", "metoda": "5", "suma": 0, "mena": "EUR", "result": "O"}' },
  ]},
  { category: 'Auth', endpoints: [
    { name: 'SetPassword', params: '?StrankaId=', body: '{"osobaId": 0, "password": ""}' },
    { name: 'SetUserName', params: '?StrankaId=', body: '{"osobaId": 0, "userName": ""}' },
  ]},
]

export default {
  name: 'api-tester',
  data() {
    return {
      reqMethod: 'read',
      endpoint: '',
      params: '',
      body: '',
      loading: false,
      response: null,
      error: null,
      responseTime: 0,
      history: [],
      readCategories: READ_ENDPOINTS,
      writeCategories: WRITE_ENDPOINTS,
    }
  },
  computed: {
    formattedData() {
      if (!this.response) return ''
      try {
        const data = this.response.data || this.response
        return JSON.stringify(data, null, 2)
      } catch (e) {
        return String(this.response)
      }
    },
    responseSize() {
      if (!this.formattedData) return ''
      const bytes = new Blob([this.formattedData]).size
      if (bytes > 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB'
      if (bytes > 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return bytes + ' B'
    },
    filteredEndpoints() {
      const all = []
      READ_ENDPOINTS.forEach(c => c.endpoints.forEach(e => all.push(e.name)))
      WRITE_ENDPOINTS.forEach(c => c.endpoints.forEach(e => all.push(e.name)))
      if (!this.endpoint) return all
      return all.filter(e => e.toLowerCase().includes(this.endpoint.toLowerCase()))
    }
  },
  methods: {
    async sendRequest() {
      if (!this.endpoint) { this.$message.warning('Vyberte endpoint'); return }
      this.loading = true
      this.response = null
      this.error = null

      const payload = {
        method: this.reqMethod,
        endpoint: this.endpoint,
        params: this.params || ''
      }

      if (this.reqMethod === 'send' && this.body) {
        try {
          payload.body = JSON.parse(this.body)
        } catch (e) {
          this.error = 'Nevalidný JSON v body'
          this.loading = false
          return
        }
      }

      const start = Date.now()
      try {
        const res = await dataService.axiosPost('bisexplorer', payload)
        this.responseTime = Date.now() - start
        this.response = (res && res.data && res.data.data) ? res.data.data : (res && res.data) ? res.data : res

        this.history.unshift({ method: this.reqMethod, endpoint: this.endpoint, params: this.params, time: this.responseTime })
        if (this.history.length > 30) this.history.pop()
      } catch (err) {
        this.responseTime = Date.now() - start
        this.error = err.message || 'Chyba pri volaní'
      }
      this.loading = false
    },
    selectEndpoint(method, ep) {
      this.reqMethod = method
      this.endpoint = ep.name
      this.params = ep.params || ''
      if (ep.body) this.body = ep.body
    },
    onEndpointSelect(val) {
      // Nájdi endpoint a predvyplň params
      const allEps = [...READ_ENDPOINTS.flatMap(c => c.endpoints), ...WRITE_ENDPOINTS.flatMap(c => c.endpoints)]
      const found = allEps.find(e => e.name === val)
      if (found) {
        this.params = found.params || ''
        if (found.body) { this.body = found.body; this.reqMethod = 'send' }
        else { this.reqMethod = 'read' }
      }
    },
    loadFromHistory(h) {
      this.reqMethod = h.method
      this.endpoint = h.endpoint
      this.params = h.params
    },
    copyResponse() {
      navigator.clipboard.writeText(this.formattedData)
      this.$message.success('Skopírované')
    }
  }
}
</script>

<style scoped>
.filter-label { display: block; font-size: 11px; font-weight: 500; color: #64748b; margin-bottom: 2px; }
.api-response { background: #1e293b; color: #e2e8f0; border-radius: 6px; padding: 16px; font-size: 12px; font-family: monospace; max-height: 500px; overflow: auto; white-space: pre-wrap; word-break: break-all; }
.history-item { padding: 6px 8px; border-bottom: 1px solid #f0f0f0; cursor: pointer; }
.history-item:hover { background: #f8fafc; }
.ep-item { padding: 3px 8px; font-size: 12px; cursor: pointer; border-radius: 3px; }
.ep-item:hover { background: #e6f7ff; color: #1890ff; }
</style>
